import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ShoppingCart, Plus, Minus, X, Pill, IndianRupee, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { WhatsAppFab } from "../components/WhatsAppFab";
import { PHARMACY } from "../constants/testIds";
import { toast } from "sonner";
import { OtpModal, loadRazorpay } from "../components/OtpModal";
import { UpiQrPayModal } from "../components/UpiQrPayModal";

export default function Pharmacy() {
  const { lang, user, cart, addToCart, updateQty, clearCart } = useApp();
  const [meds, setMeds] = useState([]);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("All");
  const [search, setSearch] = useState("");
  const [cartOpen, setCartOpen] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState("");
  const [orderId, setOrderId] = useState(null);
  const [payOrder, setPayOrder] = useState(null);
  const [couponCode, setCouponCode] = useState("");
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [couponErr, setCouponErr] = useState("");
  const [prescription, setPrescription] = useState(null); // data url
  const [otpOpen, setOtpOpen] = useState(false);
  const [otpToken, setOtpToken] = useState(null);
  const navigate = useNavigate();

  const load = async () => {
    const params = new URLSearchParams();
    if (cat && cat !== "All") params.append("category", cat);
    if (search) params.append("search", search);
    const r = await api.get(`/pharmacy/medicines?${params}`);
    setMeds(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [cat]);
  useEffect(() => { const id = setTimeout(load, 300); return () => clearTimeout(id); /* eslint-disable-next-line */ }, [search]);
  useEffect(() => { (async () => { const r = await api.get("/pharmacy/categories"); setCats(["All", ...r.data]); })(); }, []);

  const subtotal = cart.reduce((s, i) => s + i.price * i.qty, 0);
  const delivery = subtotal >= 500 || subtotal === 0 ? 0 : 40;
  const total = Math.max(0, subtotal + delivery - couponDiscount);
  const hasRx = cart.some(i => meds.find(m => m.medicine_id === i.medicine_id)?.prescription_required);

  const applyCoupon = async () => {
    setCouponErr("");
    if (!couponCode.trim()) return;
    try {
      const r = await api.post("/coupons/validate", { code: couponCode.trim(), amount: subtotal, service: "pharmacy" });
      setCouponDiscount(r.data.discount);
      toast.success(`Coupon applied: −₹${r.data.discount}`);
    } catch (e) {
      setCouponDiscount(0);
      setCouponErr(e?.response?.data?.detail || "Invalid coupon");
    }
  };
  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { toast.error("Max 2MB"); return; }
    const reader = new FileReader();
    reader.onload = () => setPrescription(reader.result);
    reader.readAsDataURL(f);
  };

  const placeOrder = async (tokenOverride) => {
    if (!user) { navigate("/login"); return; }
    if (!address || !phone) { toast.error("Address & phone required"); return; }
    if (hasRx && !prescription) { toast.error(lang==="hi"?"Rx दवाओं के लिए प्रिस्क्रिप्शन अपलोड करें":"Prescription required for Rx medicines"); return; }
    const tok = tokenOverride || otpToken;
    if (!tok) { setOtpOpen(true); return; }
    try {
      const items = cart.map(({ medicine_id, qty }) => ({ medicine_id, qty }));
      const r = await api.post("/pharmacy/orders", {
        items, delivery_address: address, phone,
        prescription_url: prescription,
        coupon_code: couponDiscount > 0 ? couponCode.trim() : null,
        otp_token: tok,
      });
      setOrderId(r.data.order_id);
      const o = await api.post("/payments/create-order", { booking_id: r.data.order_id, type: "pharmacy" });
      if (o.data.mock) {
        setPayOrder(o.data);
      } else {
        await openRazorpay(o.data, r.data.order_id);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Order failed"); }
  };

  const openRazorpay = async (order, oId) => {
    const ok = await loadRazorpay();
    if (!ok) { toast.error("Payment SDK failed to load"); return; }
    const rzp = new window.Razorpay({
      key: order.key_id, amount: order.amount, currency: order.currency,
      order_id: order.order_id, name: order.merchant_name,
      description: `Order ${oId}`,
      prefill: order.prefill,
      theme: { color: "#F59E0B" },
      handler: async (resp) => {
        try {
          await api.post("/payments/verify", {
            booking_id: oId, type: "pharmacy",
            order_id: resp.razorpay_order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
          });
          clearCart();
          setCheckoutOpen(false);
          navigate(`/payment/success?order_id=${oId}`);
        } catch { navigate("/payment/failed"); }
      },
      modal: { ondismiss: () => toast.info("Payment cancelled") },
    });
    rzp.open();
  };

  const completePayment = async () => {
    try {
      await api.post("/payments/verify", { booking_id: orderId, type: "pharmacy", order_id: payOrder.order_id });
      clearCart();
      setCheckoutOpen(false); setPayOrder(null);
      navigate(`/payment/success?order_id=${orderId}`);
    } catch { navigate("/payment/failed"); }
  };

  return (
    <div className={lang === "hi" ? "lang-hi" : ""}>
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="chip bg-amber-100 text-amber-700"><Pill size={14}/> Pharmacy</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold mt-3">{lang==="hi"?"ऑनलाइन फार्मेसी":"Online Pharmacy"}</h1>
          </div>
          <button data-testid={PHARMACY.cartBtn} onClick={() => setCartOpen(true)} className="btn-primary relative">
            <ShoppingCart size={18}/> {lang==="hi"?"कार्ट":"Cart"} ({cart.length})
          </button>
        </div>

        <div className="mt-6 flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[220px] relative">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"/>
            <input data-testid={PHARMACY.search} placeholder={lang==="hi"?"दवाई खोजें...":"Search medicines..."}
              value={search} onChange={e => setSearch(e.target.value)}
              className="w-full rounded-full bg-white border border-slate-200 pl-11 pr-4 py-3 outline-none focus:border-blue-500"/>
          </div>
          <select data-testid={PHARMACY.categorySelect} value={cat} onChange={e => setCat(e.target.value)}
            className="rounded-full bg-white border border-slate-200 px-4 py-3 outline-none focus:border-blue-500">
            {cats.map(c => <option key={c}>{c}</option>)}
          </select>
        </div>

        <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {meds.map(m => (
            <div key={m.medicine_id} className="bg-white rounded-2xl border border-slate-100 card-shadow overflow-hidden flex flex-col">
              <div className="aspect-square bg-slate-50 flex items-center justify-center overflow-hidden">
                {m.image ? <img src={m.image} alt={m.name} className="w-full h-full object-cover"/> : <Pill size={40} className="text-slate-300"/>}
              </div>
              <div className="p-4 flex flex-col flex-1">
                <div className="text-xs text-slate-500">{m.category}</div>
                <h3 className="font-medium mt-1 line-clamp-2">{m.name}</h3>
                <p className="text-xs text-slate-500 mt-1">{m.manufacturer}</p>
                {m.prescription_required && <span className="chip bg-red-50 text-red-600 mt-2 w-fit">Rx</span>}
                <div className="mt-3 flex items-center justify-between">
                  <div>
                    <div className="font-heading font-semibold text-blue-600">₹{m.price}</div>
                    <div className="text-xs text-slate-400 line-through">₹{m.mrp}</div>
                  </div>
                  <button data-testid={PHARMACY.addToCart(m.medicine_id)} onClick={() => { addToCart(m); toast.success(`${m.name} added`); }}
                    className="w-9 h-9 rounded-full bg-blue-600 text-white hover:bg-blue-700 active:scale-95 flex items-center justify-center">
                    <Plus size={18}/>
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Cart Drawer */}
      {cartOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/50" onClick={() => setCartOpen(false)}/>
          <div className="relative w-full max-w-md bg-white h-full flex flex-col">
            <div className="p-5 border-b flex items-center justify-between">
              <h3 className="font-heading text-lg font-semibold">{lang==="hi"?"आपका कार्ट":"Your Cart"}</h3>
              <button onClick={() => setCartOpen(false)}><X/></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-3">
              {cart.length === 0 && <p className="text-slate-500 text-sm">{lang==="hi"?"कार्ट खाली है":"Cart is empty"}</p>}
              {cart.map(i => (
                <div key={i.medicine_id} className="flex items-center gap-3 bg-slate-50 rounded-2xl p-3">
                  <img src={i.image} alt="" className="w-14 h-14 rounded-xl object-cover"/>
                  <div className="flex-1">
                    <div className="text-sm font-medium">{i.name}</div>
                    <div className="text-xs text-slate-500">₹{i.price}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => updateQty(i.medicine_id, i.qty - 1)} className="w-7 h-7 rounded-full bg-white border"><Minus size={14} className="mx-auto"/></button>
                    <span className="w-5 text-center">{i.qty}</span>
                    <button onClick={() => updateQty(i.medicine_id, i.qty + 1)} className="w-7 h-7 rounded-full bg-white border"><Plus size={14} className="mx-auto"/></button>
                  </div>
                </div>
              ))}
            </div>
            <div className="p-5 border-t space-y-2 text-sm">
              <div className="flex justify-between"><span>Subtotal</span><span>₹{subtotal}</span></div>
              <div className="flex justify-between"><span>Delivery</span><span>{delivery === 0 ? "FREE" : `₹${delivery}`}</span></div>
              {couponDiscount > 0 && <div className="flex justify-between text-emerald-600"><span>Coupon ({couponCode})</span><span>−₹{couponDiscount}</span></div>}
              <div className="flex justify-between font-semibold text-lg"><span>{lang==="hi"?"कुल":"Total"}</span><span>₹{total}</span></div>
              <button data-testid={PHARMACY.checkout} disabled={cart.length === 0}
                onClick={() => { setCartOpen(false); setCheckoutOpen(true); setPhone(user?.phone || ""); }}
                className="btn-primary w-full mt-3 disabled:opacity-50">
                {lang==="hi"?"चेकआउट":"Checkout"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Checkout Modal */}
      {checkoutOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl p-6 max-w-md w-full">
            {!payOrder ? (
              <>
                <h3 className="font-heading text-xl font-semibold">{lang==="hi"?"डिलीवरी जानकारी":"Delivery Details"}</h3>
                <div className="mt-4 space-y-3">
                  <textarea data-testid={PHARMACY.address} rows={3} value={address} onChange={e => setAddress(e.target.value)}
                    placeholder={lang==="hi"?"पूरा पता":"Full address"}
                    className="w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 outline-none focus:border-blue-500"/>
                  <input data-testid={PHARMACY.phone} value={phone} onChange={e => setPhone(e.target.value)}
                    placeholder={lang==="hi"?"मोबाइल नंबर":"Mobile number"}
                    className="w-full rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 outline-none focus:border-blue-500"/>

                  {/* Prescription upload */}
                  {hasRx && (
                    <div className="rounded-xl bg-red-50 border border-red-200 p-3">
                      <p className="text-xs text-red-600 font-medium">{lang==="hi"?"आपके कार्ट में Rx दवा है। प्रिस्क्रिप्शन ज़रूरी है।":"Cart contains Rx medicine. Prescription required."}</p>
                      <label className="mt-2 flex items-center gap-2 text-sm cursor-pointer text-blue-600">
                        <input type="file" accept="image/*,application/pdf" onChange={onFileChange} className="hidden" data-testid="rx-upload"/>
                        <span className="underline">{prescription ? (lang==="hi"?"बदलें":"Change") : (lang==="hi"?"अपलोड करें":"Upload prescription")}</span>
                        {prescription && <span className="text-xs text-emerald-600">✓ Attached</span>}
                      </label>
                    </div>
                  )}

                  {/* Coupon */}
                  <div>
                    {couponDiscount > 0 ? (
                      <button onClick={() => { setCouponCode(""); setCouponDiscount(0); }} className="text-sm text-red-500 hover:underline">Remove coupon ({couponCode})</button>
                    ) : (
                      <div className="flex gap-2">
                        <input value={couponCode} onChange={e => setCouponCode(e.target.value.toUpperCase())}
                          placeholder={lang==="hi"?"कूपन कोड":"Coupon code"}
                          data-testid="pharmacy-coupon-input"
                          className="flex-1 rounded-xl bg-slate-50 border border-slate-200 px-4 py-2 text-sm outline-none focus:border-blue-500"/>
                        <button onClick={applyCoupon} data-testid="pharmacy-coupon-apply" className="px-4 py-2 rounded-xl bg-blue-600 text-white text-sm font-medium">Apply</button>
                      </div>
                    )}
                    {couponErr && <p className="text-xs text-red-500 mt-1">{couponErr}</p>}
                  </div>
                </div>
                <div className="flex gap-2 mt-5">
                  <button onClick={() => setCheckoutOpen(false)} className="btn-outline flex-1">Cancel</button>
                  <button data-testid={PHARMACY.placeOrder} onClick={() => placeOrder()} className="btn-primary flex-1">
                    <IndianRupee size={16}/> Pay ₹{total}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 className="font-heading text-xl font-semibold">UPI Payment</h3>
                <div className="mt-3 rounded-2xl bg-slate-50 p-4 space-y-2 text-sm">
                  <div className="flex justify-between"><span>UPI ID</span><span className="font-mono">{payOrder.upi_id}</span></div>
                  <div className="flex justify-between"><span>Amount</span><span className="font-semibold">₹{payOrder.amount/100}</span></div>
                </div>
                <button onClick={completePayment} className="btn-primary w-full mt-4">
                  <CheckCircle2 size={16}/> {lang==="hi"?"भुगतान पूरा":"I've paid"}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Mock UPI QR modal (only mock mode). Live Razorpay opens inline in placeOrder. */}
      {payOrder && payOrder.mock && (
        <UpiQrPayModal
          open={!!payOrder}
          onClose={() => setPayOrder(null)}
          order={payOrder}
          lang={lang}
          description={`Pharmacy Order · ${orderId || ""}`}
          onPaid={completePayment}
        />
      )}

      <WhatsAppFab />
      <Footer />

      <OtpModal
        open={otpOpen}
        onClose={() => setOtpOpen(false)}
        phone={phone}
        lang={lang}
        onVerified={(token, ph) => {
          setOtpToken(token);
          setPhone(ph);
          setOtpOpen(false);
          placeOrder(token);
        }}
      />
    </div>
  );
}
